import { showError } from "./toast";

/**
 * Tipos de erros comuns da API
 */
export const ERROR_TYPES = {
  NETWORK: "NETWORK_ERROR",
  VALIDATION: "VALIDATION_ERROR",
  UNAUTHORIZED: "UNAUTHORIZED",
  NOT_FOUND: "NOT_FOUND",
  SERVER: "SERVER_ERROR",
  UNKNOWN: "UNKNOWN_ERROR",
};

/**
 * Mensagens de erro padrão
 */
export const ERROR_MESSAGES = {
  NETWORK_ERROR: "Erro de conexão. Verifique sua internet e tente novamente.",
  VALIDATION_ERROR: "Dados inválidos. Verifique as informações e tente novamente.",
  UNAUTHORIZED: "Sessão expirada. Por favor, faça login novamente.",
  NOT_FOUND: "Recurso não encontrado.",
  SERVER_ERROR: "Erro no servidor. Tente novamente mais tarde.",
  UNKNOWN_ERROR: "Ocorreu um erro inesperado. Tente novamente.",
  DEFAULT: "Ocorreu um erro. Por favor, tente novamente.",
};

/**
 * Identifica o tipo de erro baseado na resposta ou exceção
 * @param {Error|object} error - Objeto de erro
 * @returns {string} Tipo do erro
 */
export const getErrorType = (error) => {
  if (!error) return ERROR_TYPES.UNKNOWN;

  // Erro de rede
  if (error.message?.includes("fetch") || error.message?.includes("network")) {
    return ERROR_TYPES.NETWORK;
  }

  // Erro 401 - Não autorizado
  if (error.status === 401 || error.message?.includes("401")) {
    return ERROR_TYPES.UNAUTHORIZED;
  }

  // Erro 404 - Não encontrado
  if (error.status === 404 || error.message?.includes("404")) {
    return ERROR_TYPES.NOT_FOUND;
  }

  // Erro 400 - Validação
  if (error.status === 400 || error.message?.includes("validation")) {
    return ERROR_TYPES.VALIDATION;
  }

  // Erro 500+ - Servidor
  if (error.status >= 500) {
    return ERROR_TYPES.SERVER;
  }

  return ERROR_TYPES.UNKNOWN;
};

/**
 * Obtém mensagem de erro amigável
 * @param {Error|object} error - Objeto de erro
 * @param {string} defaultMessage - Mensagem padrão se não conseguir identificar
 * @returns {string} Mensagem de erro
 */
export const getErrorMessage = (error, defaultMessage = null) => {
  if (!error) {
    return defaultMessage || ERROR_MESSAGES.DEFAULT;
  }

  // Se já tem uma mensagem amigável, retorna
  if (error.message && typeof error.message === "string") {
    // Verifica se não é uma mensagem técnica
    if (!error.message.includes("fetch") && !error.message.includes("Failed to")) {
      return error.message;
    }
  }

  // Tenta obter mensagem da resposta da API
  if (error.response?.data?.message) {
    return error.response.data.message;
  }

  if (error.response?.data?.error) {
    return error.response.data.error;
  }

  // Usa tipo de erro para mensagem padrão
  const errorType = getErrorType(error);
  return ERROR_MESSAGES[errorType] || defaultMessage || ERROR_MESSAGES.DEFAULT;
};

/**
 * Trata erro de API e exibe notificação
 * @param {Error|object} error - Objeto de erro
 * @param {object} options - Opções adicionais
 * @param {string} options.context - Contexto onde o erro ocorreu (para logs)
 * @param {boolean} options.showToast - Se deve exibir toast (padrão: true)
 * @param {string} options.customMessage - Mensagem customizada
 * @returns {string} Mensagem de erro
 */
export const handleApiError = (error, options = {}) => {
  const { context = "", showToast = true, customMessage = null } = options;

  const errorMessage = customMessage || getErrorMessage(error);

  // Log do erro (apenas em desenvolvimento)
  if (process.env.NODE_ENV === "development" && context) {
    console.error(`[${context}]`, error);
  }

  // Exibe toast se solicitado
  if (showToast) {
    showError(errorMessage);
  }

  return errorMessage;
};

/**
 * Loga erro sem exibir toast (útil para erros silenciosos)
 * @param {Error|object} error - Objeto de erro
 * @param {string} context - Contexto onde o erro ocorreu
 */
export const logError = (error, context = "") => {
  if (process.env.NODE_ENV === "development") {
    console.error(`[${context}]`, error);
  }
  // Em produção, poderia enviar para serviço de logging (Sentry, etc.)
};

/**
 * Wrapper para funções async com tratamento de erro automático
 * @param {Function} asyncFn - Função async
 * @param {object} options - Opções de tratamento
 * @returns {Promise} Promise com tratamento de erro
 */
export const withErrorHandling = (asyncFn, options = {}) => {
  return async (...args) => {
    try {
      return await asyncFn(...args);
    } catch (error) {
      handleApiError(error, options);
      throw error; // Re-throw para permitir tratamento adicional se necessário
    }
  };
};

